import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useMemo, useState } from 'react'
import { usePermissions, useSaveRole } from '@/api/hooks'
import type { RoleView } from '@/api/types'
import { ErrorBanner, humanise } from '@/components/common'

/**
 * The four things a role does to a page, in the order a person reads them.
 *
 * <p>"Update" is the word on the screen; {@code edit} is the word in the
 * permission, and has to stay that way - it is what every endpoint is guarded
 * with and what every role already holds.</p>
 */
const VERBS = [
  { verb: 'read', label: 'Read' },
  { verb: 'create', label: 'Create' },
  { verb: 'edit', label: 'Update' },
  { verb: 'delete', label: 'Delete' },
] as const

/**
 * What each part of the application is called on this screen.
 *
 * <p>The module code is how the permission is filed; it is not always what the
 * office calls the page. Anything not named here is shown as it is written.</p>
 */
const PAGES: Record<string, string> = {
  availability: 'Availability and holds',
  billing: 'Invoices and credit notes',
  configuration: 'Settings',
  customer: 'Customers',
  enquiry: 'Enquiries',
  equipment: 'Equipment and gate passes',
  feedback: 'Feedback',
  kitchen: 'Kitchen production',
  logistics: 'Logistics and trips',
  manpower: 'Manpower',
  menu: 'Menu and items',
  operations: 'Operations tasks',
  order: 'Orders',
  organization: 'Organisation and outlets',
  packageconfig: 'Packages',
  payment: 'Payments and refunds',
  quotation: 'Quotations',
  reporting: 'Reports',
  security: 'Users and roles',
  utility: 'Electricity meters',
}

/**
 * What a role may do, page by page: read, create, update, delete.
 *
 * <p>A built-in role is read only: the application's own seed data and approval
 * matrices name these roles, and editing the permissions off one would shut
 * people out of parts of the product with no way back through the screens. Copy
 * it into a role of your own instead.</p>
 */
export function RoleDialog({
  open,
  role,
  onClose,
}: {
  open: boolean
  role?: RoleView | null
  onClose: () => void
}) {
  const { data: permissions } = usePermissions()
  const save = useSaveRole()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [approvalLimit, setApprovalLimit] = useState('')
  const [granted, setGranted] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    setCode(role?.code ?? '')
    setName(role?.name ?? '')
    setDescription(role?.description ?? '')
    setApprovalLimit(role?.approvalLimit == null ? '' : String(role.approvalLimit))
    setGranted(role?.permissions ?? [])
  }, [open, role])

  /**
   * One row a page, with the four verbs found on it and whatever else it holds.
   *
   * <p>A permission answers to the last word of its code, so `config.read` and
   * `org.read` are both the reading of their own page. What is left over -
   * approving a quotation, cancelling a bill, seeing a customer's number
   * unmasked - is not one of the four and is offered beside them rather than
   * dropped, which is the only way this screen can replace the old one without
   * quietly taking rights away.</p>
   */
  const pages = useMemo(() => {
    const groups = new Map<string, { verbs: Record<string, string>; extras: typeof permissions }>()
    for (const permission of permissions ?? []) {
      const page = groups.get(permission.module)
        ?? { verbs: {} as Record<string, string>, extras: [] as typeof permissions }
      const verb = permission.code.split('.').pop() ?? ''
      const isVerb = VERBS.some((one) => one.verb === verb) && !page.verbs[verb]
      if (isVerb) page.verbs[verb] = permission.code
      else page.extras?.push(permission)
      groups.set(permission.module, page)
    }
    return [...groups.entries()]
      .map(([module, page]) => ({ module, ...page }))
      .sort((left, right) =>
        (PAGES[left.module] ?? left.module).localeCompare(PAGES[right.module] ?? right.module))
  }, [permissions])

  const everyCode = (permissions ?? []).map((permission) => permission.code)
  const hold = (codes: string[], on: boolean) =>
    setGranted((current) =>
      on
        ? [...new Set([...current, ...codes])]
        : current.filter((held) => !codes.includes(held)))

  const readOnly = Boolean(role?.systemRole)
  const valid = Boolean(code.trim() && name.trim()) && !readOnly

  const toggle = (permissionCode: string) =>
    setGranted((current) =>
      current.includes(permissionCode)
        ? current.filter((held) => held !== permissionCode)
        : [...current, permissionCode],
    )


  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{role ? role.name : 'New role'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        {readOnly ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {role?.name} is built in and cannot be edited — the application relies on it. Create a role
            of your own if you need different permissions.
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase().replace(/\s+/g, '_'))}
              disabled={Boolean(role)}
              helperText={role ? 'Codes cannot change' : 'e.g. BANQUET_MANAGER'}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Approval limit"
              value={approvalLimit}
              onChange={(event) => setApprovalLimit(event.target.value)}
              disabled={readOnly}
              helperText="Above this, an approval goes further up"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={readOnly}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
          <Typography variant="subtitle2">Rights</Typography>
          <Typography variant="caption" color="text.secondary">
            {granted.length} of {everyCode.length} granted
          </Typography>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={granted.length === everyCode.length && everyCode.length > 0}
                      indeterminate={granted.length > 0 && granted.length < everyCode.length}
                      onChange={(event) => hold(everyCode, event.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label={<Typography variant="caption">SELECT ALL</Typography>}
                />
              </TableCell>
              {/* Ticking a column head gives the role that one right on every
                  page: "read everything" is a real answer, and asking somebody
                  to tick twenty boxes for it is not. */}
              {VERBS.map(({ verb, label }) => {
                const column = pages
                  .map((page) => page.verbs[verb])
                  .filter(Boolean) as string[]
                const held = column.filter((code) => granted.includes(code)).length
                return (
                  <TableCell key={verb} width={110}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={held === column.length && column.length > 0}
                          indeterminate={held > 0 && held < column.length}
                          onChange={(event) => hold(column, event.target.checked)}
                          disabled={readOnly}
                        />
                      }
                      label={<Typography variant="caption">{label}</Typography>}
                    />
                  </TableCell>
                )
              })}
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  Beyond the four
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((page) => (
              <TableRow key={page.module} hover>
                <TableCell>
                  <Typography variant="body2">{PAGES[page.module] ?? humanise(page.module)}</Typography>
                </TableCell>
                {VERBS.map(({ verb, label }) => {
                  const code = page.verbs[verb]
                  return (
                    <TableCell key={verb}>
                      {/* A page that has no such right shows nothing rather
                          than a box that cannot be ticked: there is no
                          "delete" on the operations task list, and a dead
                          checkbox only invites somebody to try. */}
                      {code ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={granted.includes(code)}
                              onChange={() => toggle(code)}
                              disabled={readOnly}
                            />
                          }
                          label={<Typography variant="caption">{label}</Typography>}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )
                })}
                <TableCell>
                  <Stack direction="row" flexWrap="wrap" useFlexGap>
                    {(page.extras ?? []).map((permission) => (
                      <Tooltip
                        key={permission.code}
                        title={permission.description ?? permission.code}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={granted.includes(permission.code)}
                              onChange={() => toggle(permission.code)}
                              disabled={readOnly}
                            />
                          }
                          label={
                            <Typography variant="caption">
                              {humanise(permission.code.split('.').slice(1).join(' '))}
                            </Typography>
                          }
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {readOnly ? null : (
          <Button
            variant="contained"
            disabled={!valid || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  id: role?.id,
                  body: {
                    code: code.trim(),
                    name: name.trim(),
                    description: description || undefined,
                    approvalLimit: approvalLimit ? Number(approvalLimit) : undefined,
                    permissions: granted,
                  },
                },
                { onSuccess: onClose },
              )
            }
          >
            {role ? 'Save role' : 'Create role'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
